import { resolveReasonLabel } from './resolveReason';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Alert } from '@detect/shared';
import { alertService } from '../../services/alerts';
import type { WithId } from '../../services/types';
import { useTenant } from '../../contexts/TenantContext';
import { useTenantQuery } from '../../hooks/useApi';
import { LoadingSpinner, ErrorState } from '../../components/common';
import { formatDateTime, formatRelativeTime } from '../../utils/formatters';

export function AlertDetailPage() {
  const { id = '' } = useParams();
  const { tenantId, hasRole } = useTenant();
  const [note, setNote] = useState('');
  const [actionError, setActionError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const query = useTenantQuery<WithId<Alert>>(['alert', id], `/alerts/${id}`, {}, {
    refetchIntervalMs: 30_000,
  });

  if (query.isLoading) return <LoadingSpinner />;
  if (query.isError || !query.data) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  const alert = query.data;

  const act = async (action: 'ack' | 'resolve' | 'note') => {
    if (!tenantId) return;
    setBusy(true);
    setActionError(null);
    try {
      if (action === 'ack') await alertService.acknowledge(tenantId, alert.id, note || undefined);
      if (action === 'resolve') await alertService.resolve(tenantId, alert.id, note || undefined);
      if (action === 'note') await alertService.addNote(tenantId, alert.id, note);
      setNote('');
      void query.refetch();
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(false);
    }
  };

  const submitNote = (e: FormEvent) => {
    e.preventDefault();
    if (note.trim().length > 0) void act('note');
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">
          <Link to="/alerts">Alerts</Link> /{' '}
          <span className={`severity-badge severity-${alert.severity}`}>
            {alert.severity}
          </span>{' '}
          {alert.resourceName}: {alert.sensorKey}/{alert.datapoint}
        </h1>
        {hasRole('agent') && (
          <div className="page-actions">
            {alert.status === 'open' && (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy}
                onClick={() => void act('ack')}
              >
                Acknowledge
              </button>
            )}
            {alert.status !== 'resolved' && (
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy}
                onClick={() => void act('resolve')}
              >
                Resolve
              </button>
            )}
          </div>
        )}
      </div>
      {actionError !== null && <ErrorState error={actionError} />}

      <div className="card">
        <dl className="detail-grid">
          <dt>Status</dt>
          <dd>{alert.status}</dd>
          <dt>Resource</dt>
          <dd>
            <Link to={`/resources/${alert.resourceId}`}>{alert.resourceName}</Link>
          </dd>
          <dt>Value</dt>
          <dd>{alert.lastValue ?? 'no data'}</dd>
          <dt>Threshold</dt>
          <dd>
            {alert.thresholdSnapshot
              ? `${alert.thresholdSnapshot.operator} ${
                  alert.thresholdSnapshot[alert.severity] ?? ''
                }`
              : 'no-data condition'}
          </dd>
          <dt>Started</dt>
          <dd>{formatDateTime(alert.startedAt)}</dd>
          <dt>Last occurred</dt>
          <dd>
            {formatRelativeTime(alert.lastOccurredAt)} ({alert.occurrenceCount}{' '}
            occurrences)
          </dd>
          {alert.acknowledgedAt && (
            <>
              <dt>Acknowledged</dt>
              <dd>
                {formatDateTime(alert.acknowledgedAt)} by {alert.acknowledgedBy}
              </dd>
            </>
          )}
          {alert.resolvedAt && (
            <>
              <dt>Resolved</dt>
              <dd>
                {formatDateTime(alert.resolvedAt)} ({resolveReasonLabel(alert.resolveReason)})
              </dd>
            </>
          )}
          {alert.dispatchRequestId && (
            <>
              <dt>Dispatch request</dt>
              <dd>{alert.dispatchRequestId}</dd>
            </>
          )}
          {alert.deskAlertId && (
            <>
              <dt>Desk alert</dt>
              <dd>{alert.deskAlertId}</dd>
            </>
          )}
          {alert.deskTicketId && (
            <>
              <dt>Desk ticket</dt>
              <dd>{alert.deskTicketId}</dd>
            </>
          )}
        </dl>
      </div>

      <h2>Notes</h2>
      <div className="card">
        {alert.notes.length === 0 ? (
          <p className="text-muted">No notes yet.</p>
        ) : (
          <ul className="note-list">
            {alert.notes.map((n, i) => (
              <li key={i} className="note-item">
                <div className="note-meta">
                  {n.userId} · {formatDateTime(n.createdAt)}
                </div>
                <div>{n.note}</div>
              </li>
            ))}
          </ul>
        )}
        {hasRole('agent') && (
          <form onSubmit={submitNote} className="note-form">
            <label htmlFor="alert-note" className="visually-hidden">
              Add note
            </label>
            <textarea
              id="alert-note"
              name="note"
              rows={2}
              placeholder="Add a note (also attached when acknowledging/resolving)…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <button
              type="submit"
              className="btn btn-secondary"
              disabled={busy || note.trim().length === 0}
            >
              Add note
            </button>
          </form>
        )}
      </div>

      <h2>Deliveries</h2>
      <div className="card">
        {alert.deliveries.length === 0 ? (
          <p className="text-muted">No notifications delivered yet.</p>
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Channel</th>
                  <th>Event</th>
                  <th>Status</th>
                  <th>Attempts</th>
                  <th>Last attempt</th>
                  <th>Reference</th>
                </tr>
              </thead>
              <tbody>
                {alert.deliveries.map((d) => (
                  <tr key={d.slotId}>
                    <td>{d.channel}</td>
                    <td>{d.eventKind}</td>
                    <td>{d.status}</td>
                    <td>{d.attempts}</td>
                    <td>{d.lastAttemptAt ? formatRelativeTime(d.lastAttemptAt) : '—'}</td>
                    <td>{d.externalRef ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
